/**
 * WeaveIn.ai Platform - Workflows Hook
 * React hook for workflow template and instance management
 */

import { useState, useEffect, useCallback } from 'react';
import {
  workflowsAPI,
  WorkflowTemplate,
  WorkflowTemplateCreate,
  WorkflowTemplateListResponse,
  WorkflowInstance,
  WorkflowInstanceCreate,
  WorkflowInstanceListResponse,
  WorkflowInstanceStatus,
  WorkflowStep,
  WorkflowStepCreate,
  WorkflowStepExecution,
} from '@/services/api/workflows';

interface UseWorkflowTemplatesOptions {
  page?: number;
  page_size?: number;
  program_id?: number;
  is_active?: boolean;
  autoFetch?: boolean;
}

interface UseWorkflowTemplatesReturn {
  templates: WorkflowTemplate[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  createTemplate: (data: WorkflowTemplateCreate) => Promise<WorkflowTemplate | null>;
  updateTemplate: (id: number, data: Partial<WorkflowTemplateCreate>) => Promise<WorkflowTemplate | null>;
  deleteTemplate: (id: number) => Promise<boolean>;
  getTemplate: (id: number) => Promise<WorkflowTemplate | null>;
  refetch: () => Promise<void>;
  setPage: (page: number) => void;
  setFilters: (filters: { program_id?: number; is_active?: boolean }) => void;
}

export default function useWorkflowTemplates(
  options: UseWorkflowTemplatesOptions = {}
): UseWorkflowTemplatesReturn {
  const {
    page: initialPage = 1,
    page_size = 50,
    program_id: initialProgramId,
    is_active: initialIsActive,
    autoFetch = true,
  } = options;

  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [programId, setProgramId] = useState(initialProgramId);
  const [isActive, setIsActive] = useState(initialIsActive);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response: WorkflowTemplateListResponse = await workflowsAPI.listWorkflowTemplates({
        page,
        page_size,
        program_id: programId,
        is_active: isActive,
      });

      setTemplates(response.workflows);
      setTotal(response.total);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch workflow templates';
      setError(errorMessage);
      console.error('Error fetching workflow templates:', err);
    } finally {
      setLoading(false);
    }
  }, [page, page_size, programId, isActive]);

  useEffect(() => {
    if (autoFetch) {
      fetchTemplates();
    }
  }, [autoFetch, fetchTemplates]);

  const createTemplate = async (data: WorkflowTemplateCreate): Promise<WorkflowTemplate | null> => {
    setLoading(true);
    setError(null);

    try {
      const newTemplate = await workflowsAPI.createWorkflowTemplate(data);

      // Optimistic update - add to list
      setTemplates((prev) => [newTemplate, ...prev]);
      setTotal((prev) => prev + 1);

      return newTemplate;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create workflow template';
      setError(errorMessage);
      console.error('Error creating workflow template:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateTemplate = async (
    id: number,
    data: Partial<WorkflowTemplateCreate>
  ): Promise<WorkflowTemplate | null> => {
    setLoading(true);
    setError(null);

    try {
      const updatedTemplate = await workflowsAPI.updateWorkflowTemplate(id, data);

      // Update in local state
      setTemplates((prev) =>
        prev.map((template) => (template.id === id ? updatedTemplate : template))
      );

      return updatedTemplate;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update workflow template';
      setError(errorMessage);
      console.error('Error updating workflow template:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (id: number): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await workflowsAPI.deleteWorkflowTemplate(id);

      // Remove from local state
      setTemplates((prev) => prev.filter((template) => template.id !== id));
      setTotal((prev) => prev - 1);

      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete workflow template';
      setError(errorMessage);
      console.error('Error deleting workflow template:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getTemplate = async (id: number): Promise<WorkflowTemplate | null> => {
    setLoading(true);
    setError(null);

    try {
      const template = await workflowsAPI.getWorkflowTemplate(id);
      return template;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch workflow template';
      setError(errorMessage);
      console.error('Error fetching workflow template:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const setFilters = (filters: { program_id?: number; is_active?: boolean }) => {
    setProgramId(filters.program_id);
    setIsActive(filters.is_active);
    setPage(1); // Reset to first page when filters change
  };

  return {
    templates,
    total,
    page,
    pageSize: page_size,
    loading,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplate,
    refetch: fetchTemplates,
    setPage,
    setFilters,
  };
}

// ============================================================================
// WORKFLOW INSTANCES HOOK
// ============================================================================

interface UseWorkflowInstancesOptions {
  page?: number;
  page_size?: number;
  workflow_template_id?: number;
  enrollment_id?: number;
  status?: WorkflowInstanceStatus;
  autoFetch?: boolean;
}

interface UseWorkflowInstancesReturn {
  instances: WorkflowInstance[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  createInstance: (data: WorkflowInstanceCreate) => Promise<WorkflowInstance | null>;
  updateInstance: (id: number, data: Partial<WorkflowInstanceCreate>) => Promise<WorkflowInstance | null>;
  deleteInstance: (id: number) => Promise<boolean>;
  getInstance: (id: number) => Promise<WorkflowInstance | null>;
  refetch: () => Promise<void>;
  setPage: (page: number) => void;
  setFilters: (filters: {
    workflow_template_id?: number;
    enrollment_id?: number;
    status?: WorkflowInstanceStatus;
  }) => void;
}

export function useWorkflowInstances(
  options: UseWorkflowInstancesOptions = {}
): UseWorkflowInstancesReturn {
  const {
    page: initialPage = 1,
    page_size = 50,
    workflow_template_id: initialTemplateId,
    enrollment_id: initialEnrollmentId,
    status: initialStatus,
    autoFetch = true,
  } = options;

  const [instances, setInstances] = useState<WorkflowInstance[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [templateId, setTemplateId] = useState(initialTemplateId);
  const [enrollmentId, setEnrollmentId] = useState(initialEnrollmentId);
  const [status, setStatus] = useState(initialStatus);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInstances = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response: WorkflowInstanceListResponse = await workflowsAPI.listWorkflowInstances({
        page,
        page_size,
        workflow_template_id: templateId,
        enrollment_id: enrollmentId,
        status,
      });

      setInstances(response.instances);
      setTotal(response.total);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch workflow instances';
      setError(errorMessage);
      console.error('Error fetching workflow instances:', err);
    } finally {
      setLoading(false);
    }
  }, [page, page_size, templateId, enrollmentId, status]);

  useEffect(() => {
    if (autoFetch) {
      fetchInstances();
    }
  }, [autoFetch, fetchInstances]);

  const createInstance = async (data: WorkflowInstanceCreate): Promise<WorkflowInstance | null> => {
    setLoading(true);
    setError(null);

    try {
      const newInstance = await workflowsAPI.createWorkflowInstance(data);

      // Optimistic update - add to list
      setInstances((prev) => [newInstance, ...prev]);
      setTotal((prev) => prev + 1);

      return newInstance;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create workflow instance';
      setError(errorMessage);
      console.error('Error creating workflow instance:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateInstance = async (
    id: number,
    data: Partial<WorkflowInstanceCreate>
  ): Promise<WorkflowInstance | null> => {
    setLoading(true);
    setError(null);

    try {
      const updatedInstance = await workflowsAPI.updateWorkflowInstance(id, data);

      // Update in local state
      setInstances((prev) =>
        prev.map((instance) => (instance.id === id ? updatedInstance : instance))
      );

      return updatedInstance;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update workflow instance';
      setError(errorMessage);
      console.error('Error updating workflow instance:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteInstance = async (id: number): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await workflowsAPI.deleteWorkflowInstance(id);

      // Remove from local state
      setInstances((prev) => prev.filter((instance) => instance.id !== id));
      setTotal((prev) => prev - 1);

      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete workflow instance';
      setError(errorMessage);
      console.error('Error deleting workflow instance:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getInstance = async (id: number): Promise<WorkflowInstance | null> => {
    setLoading(true);
    setError(null);

    try {
      const instance = await workflowsAPI.getWorkflowInstance(id);
      return instance;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch workflow instance';
      setError(errorMessage);
      console.error('Error fetching workflow instance:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const setFilters = (filters: {
    workflow_template_id?: number;
    enrollment_id?: number;
    status?: WorkflowInstanceStatus;
  }) => {
    setTemplateId(filters.workflow_template_id);
    setEnrollmentId(filters.enrollment_id);
    setStatus(filters.status);
    setPage(1); // Reset to first page when filters change
  };

  return {
    instances,
    total,
    page,
    pageSize: page_size,
    loading,
    error,
    createInstance,
    updateInstance,
    deleteInstance,
    getInstance,
    refetch: fetchInstances,
    setPage,
    setFilters,
  };
}

// ============================================================================
// WORKFLOW STEPS HOOK
// ============================================================================

export function useWorkflowSteps(templateId: number) {
  const [steps, setSteps] = useState<WorkflowStep[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSteps = useCallback(async () => {
    if (!templateId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await workflowsAPI.listWorkflowSteps(templateId);
      setSteps(data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch workflow steps';
      setError(errorMessage);
      console.error('Error fetching workflow steps:', err);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    fetchSteps();
  }, [fetchSteps]);

  const createStep = async (data: WorkflowStepCreate): Promise<WorkflowStep | null> => {
    try {
      const newStep = await workflowsAPI.createWorkflowStep(data);
      setSteps((prev) => [...prev, newStep].sort((a, b) => a.step_order - b.step_order));
      return newStep;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create workflow step';
      setError(errorMessage);
      return null;
    }
  };

  const updateStep = async (id: number, data: Partial<WorkflowStepCreate>): Promise<WorkflowStep | null> => {
    try {
      const updated = await workflowsAPI.updateWorkflowStep(id, data);
      setSteps((prev) => prev.map((s) => (s.id === id ? updated : s)).sort((a, b) => a.step_order - b.step_order));
      return updated;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update workflow step';
      setError(errorMessage);
      return null;
    }
  };

  const deleteStep = async (id: number): Promise<boolean> => {
    try {
      await workflowsAPI.deleteWorkflowStep(id);
      setSteps((prev) => prev.filter((s) => s.id !== id));
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete workflow step';
      setError(errorMessage);
      return false;
    }
  };

  return {
    steps,
    loading,
    error,
    createStep,
    updateStep,
    deleteStep,
    refetch: fetchSteps,
  };
}

// ============================================================================
// WORKFLOW STEP EXECUTIONS HOOK
// ============================================================================

export function useWorkflowExecutions(instanceId: number) {
  const [executions, setExecutions] = useState<WorkflowStepExecution[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchExecutions = useCallback(async () => {
    if (!instanceId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await workflowsAPI.listStepExecutions(instanceId);
      setExecutions(data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch step executions';
      setError(errorMessage);
      console.error('Error fetching step executions:', err);
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => {
    fetchExecutions();
  }, [fetchExecutions]);

  return {
    executions,
    loading,
    error,
    refetch: fetchExecutions,
  };
}
